import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { useCart } from '../context/CartContext';
import { useStateContext } from '../hooks/useStateContext';
import { track } from '../utils/track';

const STATUS_LABELS = { not_started: 'Not Started', in_progress: 'In Progress', completed: 'Completed' };
const STATUS_COLORS = { not_started: 'badge-slate', in_progress: 'badge-warning', completed: 'badge-success' };

export default function MyCoursesPage() {
  const [courses, setCourses] = useState([]);
  const [archivedCourses, setArchivedCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const justPurchased = searchParams.get('success') === 'true';
  const sessionId = searchParams.get('session_id');
  const { fetchCart, clearBundle } = useCart();
  const { state } = useStateContext();

  useEffect(() => {
    const load = async () => {
      if (sessionId) {
        try {
          await api.get(`/payments/verify/${sessionId}`);
          // Funnel: count each Stripe session once, even if this page is refreshed.
          try {
            const k = `se_tracked_${sessionId}`;
            if (!sessionStorage.getItem(k)) { track('purchase', sessionId.slice(-12)); sessionStorage.setItem(k, '1'); }
          } catch { /* ignore */ }
          localStorage.removeItem('se_cart_bundle');
          clearBundle();
          await fetchCart();
        } catch {}
      }
      const r = await api.get('/users/me/courses');
      setCourses(r.data.courses);
      setArchivedCourses(r.data.archivedCourses || []);
      setLoading(false);
    };
    load();
  }, [sessionId]);

  if (loading) return <div className="loading-wrapper"><div className="spinner" /></div>;

  // Split courses into active (not completed) and completed
  const activeCourses = courses.filter(c => c.status !== 'completed');
  // Combine currently-completed courses with archived completions for history display
  const completedCourses = [...courses.filter(c => c.status === 'completed'), ...archivedCourses];

  // Group completed courses by year
  const completedByYear = {};
  completedCourses.forEach(c => {
    const year = c.completed_at ? new Date(c.completed_at).getFullYear() : 'Unknown';
    if (!completedByYear[year]) completedByYear[year] = [];
    completedByYear[year].push(c);
  });
  const sortedYears = Object.keys(completedByYear).sort((a, b) => b - a);

  const handleDownloadCert = (course) => {
    fetch(`${process.env.REACT_APP_API_URL || '/api'}/users/me/courses/${course.course_id}/certificate`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('se_token')}` },
    })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `SteadyEddy-Certificate-${course.title.replace(/[^a-zA-Z0-9]/g, '-')}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
      });
  };

  return (
    <div className="page-wrapper">
      <div className="container">
        {justPurchased && (
          <div className="alert alert-success" style={{ marginBottom: 24 }}>
            🎉 Purchase complete! Your courses are now available below.
          </div>
        )}

        <div style={styles.pageHeader}>
          <div>
            <h1 className="page-title">My Courses</h1>
            <p className="page-subtitle">{courses.length + archivedCourses.length} course{(courses.length + archivedCourses.length) !== 1 ? 's' : ''} purchased</p>
          </div>
          <Link to={`/${state.slug}/courses`} className="btn btn-secondary">Browse More Courses</Link>
        </div>

        {courses.length === 0 && archivedCourses.length === 0 ? (
          <div style={styles.empty}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>📚</div>
            <h2>No courses yet</h2>
            <p style={{ color: 'var(--color-text-muted)', marginBottom: 24 }}>Purchase a course to get started with your Arizona CE requirements.</p>
            <Link to={`/${state.slug}/courses`} className="btn btn-primary btn-lg">Browse Courses</Link>
          </div>
        ) : (
          <>
            {/* Active / In-Progress Courses */}
            {activeCourses.length > 0 && (
              <>
                <div style={styles.grid}>
                  {activeCourses.map(course => (
                    <div key={course.id} style={styles.courseCard}>
                      <div style={styles.cardThumb}>
                        {course.thumbnail_url
                          ? <img src={course.thumbnail_url} alt={course.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <div style={styles.thumbFallback}>📖</div>
                        }
                        <span className={`badge ${STATUS_COLORS[course.status]}`} style={styles.statusBadge}>
                          {STATUS_LABELS[course.status]}
                        </span>
                      </div>
                      <div style={styles.cardBody}>
                        <h3 style={styles.cardTitle}>{course.title}</h3>
                        <p style={styles.cardMeta}>{course.credit_hours} credit hours • {course.adre_course_number}</p>
                        <div style={{ marginBottom: 16 }}>
                          <div style={styles.progressRow}>
                            <span style={styles.progressLabel}>Progress</span>
                            <span style={styles.progressPct}>{course.progress_percent || 0}%</span>
                          </div>
                          <div className="progress-bar">
                            <div className="progress-fill" style={{ width: `${course.progress_percent || 0}%` }} />
                          </div>
                        </div>
                        <div style={styles.dates}>
                          <div style={styles.dateItem}>
                            <span style={styles.dateLabel}>Started</span>
                            <span style={styles.dateValue}>{new Date(course.purchased_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div style={styles.cardFooter}>
                          <div />
                          <Link to={`/course/${course.course_id}/learn`} className="btn btn-primary btn-sm">
                            {course.status === 'not_started' ? 'Start' : 'Continue'} →
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Completed Courses History */}
            {sortedYears.length > 0 && (
              <div style={{ marginTop: activeCourses.length > 0 ? 48 : 0 }}>
                {sortedYears.map(year => (
                  <div key={year} style={{ marginBottom: 32 }}>
                    <div style={styles.yearHeader}>
                      <div style={styles.yearBadge}>{year}</div>
                      <span style={styles.yearLabel}>
                        Completed — {completedByYear[year].length} course{completedByYear[year].length !== 1 ? 's' : ''} •{' '}
                        {completedByYear[year].reduce((sum, c) => sum + parseFloat(c.credit_hours || 0), 0)} credit hours
                      </span>
                      <div style={styles.yearLine} />
                    </div>

                    <div style={styles.completedList}>
                      {completedByYear[year].map(course => (
                        <div key={course.id} style={styles.completedRow}>
                          <div style={styles.completedLeft}>
                            <div style={styles.completedCheck}>✓</div>
                            <div>
                              <div style={styles.completedTitle}>{course.title}</div>
                              <div style={styles.completedMeta}>
                                {course.adre_course_number} • {course.credit_hours} credits • Started {new Date(course.purchased_at).toLocaleDateString()} • Completed {new Date(course.completed_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                          <div style={styles.completedActions}>
                            {!course.is_archived && (
                              <button
                                className="btn btn-ghost btn-sm"
                                style={styles.certBtn}
                                onClick={() => handleDownloadCert(course)}
                              >
                                Certificate
                              </button>
                            )}
                            {!course.is_archived && (
                              <Link to={`/course/${course.course_id}/learn`} className="btn btn-ghost btn-sm" style={styles.reviewBtn}>
                                Review →
                              </Link>
                            )}
                            {course.is_archived && (
                              <span style={{ fontSize: 12, color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                                Previous cycle
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  pageHeader: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 32, flexWrap: 'wrap', gap: 16 },
  empty: { textAlign: 'center', padding: '80px 20px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(240px, 100%), 1fr))', gap: 20 },
  courseCard: { background: 'white', border: '1px solid var(--color-border)', borderRadius: 16, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  cardThumb: { height: 140, background: 'linear-gradient(135deg, #0f766e, #14b8a6)', position: 'relative', overflow: 'hidden' },
  thumbFallback: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40 },
  statusBadge: { position: 'absolute', top: 10, right: 10 },
  cardBody: { padding: '20px', flex: 1, display: 'flex', flexDirection: 'column', gap: 10 },
  cardTitle: { fontSize: 16, fontWeight: 700, color: 'var(--slate-900)', lineHeight: 1.3 },
  cardMeta: { fontSize: 12, color: 'var(--teal-600)', fontWeight: 600 },
  progressRow: { display: 'flex', justifyContent: 'space-between', marginBottom: 6 },
  progressLabel: { fontSize: 13, color: 'var(--color-text-muted)' },
  progressPct: { fontSize: 13, fontWeight: 700, color: 'var(--color-primary)' },
  dates: { display: 'flex', gap: 16, flexWrap: 'wrap' },
  dateItem: { display: 'flex', flexDirection: 'column', gap: 2 },
  dateLabel: { fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.03em' },
  dateValue: { fontSize: 13, color: 'var(--slate-700)', fontWeight: 600 },
  cardFooter: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', paddingTop: 12, borderTop: '1px solid var(--color-border)' },
  // Completed history styles
  yearHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 },
  yearBadge: { background: 'var(--forest-600)', color: 'white', fontSize: 13, fontWeight: 700, padding: '4px 12px', borderRadius: 6, flexShrink: 0 },
  yearLabel: { fontSize: 13, color: 'var(--color-text-muted)', fontWeight: 500, flexShrink: 0 },
  yearLine: { flex: 1, height: 1, background: 'var(--color-border)' },
  completedList: { display: 'flex', flexDirection: 'column', gap: 8 },
  completedRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, background: 'white', border: '1px solid var(--color-border)', borderRadius: 12, padding: '16px 20px', flexWrap: 'wrap' },
  completedLeft: { display: 'flex', alignItems: 'center', gap: 14, minWidth: 0, flex: 1 },
  completedCheck: { width: 32, height: 32, borderRadius: '50%', background: 'var(--forest-50)', color: 'var(--forest-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, flexShrink: 0 },
  completedTitle: { fontSize: 15, fontWeight: 600, color: 'var(--slate-900)' },
  completedMeta: { fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 },
  completedActions: { display: 'flex', gap: 8, flexShrink: 0 },
  certBtn: { color: 'var(--forest-600)', borderColor: 'var(--forest-200)', fontSize: 13 },
  reviewBtn: { color: 'var(--slate-600)', borderColor: 'var(--slate-200)', fontSize: 13 },
};
